import { useCallback, useEffect, useState } from 'react';
import type { InfoECGSelectionProps } from './infoECGSelection.types';

/**
 * Keeps the technician comments area of an event row sized to its ECG strip. Measures the available comment rows on mount and re-measures through a ResizeObserver attached to the strip element, disconnecting it on cleanup, and exposes the resulting row count.
 *
 * @param eventId - Event id identifying the ECG strip element to measure.
 * @returns Number of comment rows that fit beside the strip.
 */
export const useInfoECGSelection = ({ eventId }: Pick<InfoECGSelectionProps, 'eventId'>) => {
  const [commentRows, setCommentRows] = useState<number>();

  /**
   * Measures how many text rows the technician comments block may occupy beside an event's ECG strip. Looks the strip container up by event id and bails out when it is not mounted, otherwise subtracts the symptoms, margin and comments-header offsets from its height and divides by the 22px line height into the comment rows state.
   *
   * @returns Undefined when the strip container is not mounted.
   */
  const calculateTechnicianCommentsRows = useCallback((): any => {
    const element = document.getElementById(`slider-image-ecg-${eventId}`);
    if (!element) {
      return;
    }

    const lineHeight = 22;
    const symtomsHeight = 42;
    const marginTop = 8;
    const technicianCommentsHeaderHeight = 20;
    const offset = symtomsHeight + marginTop + technicianCommentsHeaderHeight;
    setCommentRows((element.clientHeight - offset) / lineHeight);
  }, [eventId]);

  useEffect(() => {
    calculateTechnicianCommentsRows();
    const element = document.getElementById(`slider-image-ecg-${eventId}`);
    if (!element) {
      return undefined;
    }

    /**
     * Watches the event's ECG strip container so the comments row count follows its size. A ResizeObserver whose callback re-runs the technician comments row measurement on every resize.
     *
     * @returns Observer re-measuring the comment rows.
     */
    const observer = new ResizeObserver(() => {
      calculateTechnicianCommentsRows();
    });
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, [calculateTechnicianCommentsRows, eventId]);

  return { commentRows };
};
